import { useState } from "react";
import type { Invoice, PaidReadyView } from "../../api/paidReady";
import { BackButton } from "../../components/BackButton";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Checks } from "../../components/Checks";
import { Field, inputShell } from "../../components/Field";
import { Guide } from "../../components/Guide";
import { ScreenHeader } from "../../components/ScreenHeader";
import { money, numberOrNull, shortDate, wordCount } from "../../lib/format";

interface InvoiceScreenProps {
  view: PaidReadyView;
  fullName: string;
  email: string;
  busy: boolean;
  message: string | null;
  onChange: (invoice: Invoice) => void;
  onSave: () => void;
}

/** The invoice builder with a live, printable preview. */
export function InvoiceScreen({
  view,
  fullName,
  email,
  busy,
  message,
  onChange,
  onSave,
}: InvoiceScreenProps) {
  const [invoice, setInvoice] = useState<Invoice>(view.invoice);
  const passes = view.invoiceChecks.every((check) => check.pass);
  const items = invoice.items.filter(
    (item) => wordCount(item.description) >= 2 && (item.amount ?? 0) > 0,
  );
  const total = items.reduce((sum, item) => sum + (item.amount ?? 0), 0);
  const today = new Date();
  const due = new Date(today.getTime() + invoice.dueDays * 864e5);

  const set = (patch: Partial<Invoice>) => {
    const next = { ...invoice, ...patch };
    setInvoice(next);
    onChange(next);
  };

  return (
    <div>
      <ScreenHeader
        kicker="Step 4 · Get Paid-Ready · Invoice"
        title="Your professional invoice."
        sub="A clear invoice gets you paid faster. Fill it in for a real or practice client. Reuse it for every job."
      />
      <Guide guideKey="invoice" done={view.step.invoiceDone} />
      <div className="grid grid-cols-1 gap-x-3 sm:grid-cols-3">
        <Field label="Invoice number" htmlFor="iv-no">
          <input
            id="iv-no"
            className={inputShell}
            value={invoice.no}
            onChange={(e) => set({ no: e.target.value })}
          />
        </Field>
        <Field label="Client name" htmlFor="iv-client">
          <input
            id="iv-client"
            className={inputShell}
            value={invoice.client}
            onChange={(e) => set({ client: e.target.value })}
          />
        </Field>
        <Field label="Due in" htmlFor="iv-due">
          <select
            id="iv-due"
            className={inputShell}
            value={invoice.dueDays}
            onChange={(e) => set({ dueDays: Number(e.target.value) })}
          >
            {[7, 14, 30].map((d) => (
              <option key={d} value={d}>
                {d} days
              </option>
            ))}
          </select>
        </Field>
      </div>
      {invoice.items.map((item, index) => (
        <div key={index} className="grid grid-cols-[3fr_1fr] gap-x-3">
          <Field label={`Item ${index + 1}`} htmlFor={`iv-d-${index}`}>
            <input
              id={`iv-d-${index}`}
              className={inputShell}
              placeholder={index ? "Optional" : "What you delivered"}
              value={item.description}
              onChange={(e) =>
                set({
                  items: invoice.items.map((it, i) =>
                    i === index ? { ...it, description: e.target.value } : it,
                  ),
                })
              }
            />
          </Field>
          <Field label="Amount (USD)" htmlFor={`iv-a-${index}`}>
            <input
              id={`iv-a-${index}`}
              type="number"
              min={0}
              className={inputShell}
              value={item.amount ?? ""}
              onChange={(e) =>
                set({
                  items: invoice.items.map((it, i) =>
                    i === index ? { ...it, amount: numberOrNull(e.target.value) } : it,
                  ),
                })
              }
            />
          </Field>
        </div>
      ))}
      <Field label="How to pay you" htmlFor="iv-pay" hint="Don't type full account numbers here.">
        <input
          id="iv-pay"
          className={inputShell}
          placeholder="e.g. Payoneer or bank transfer, details on request"
          value={invoice.payVia}
          onChange={(e) => set({ payVia: e.target.value })}
        />
      </Field>
      <Checks checks={view.invoiceChecks} />
      <div className="doc my-4 rounded-[var(--radius-card)] border border-border bg-surface p-5 text-[14px]">
        <div className="flex justify-between gap-3">
          <div>
            <h2 className="text-[22px] text-accent">Invoice</h2>
            <div className="text-[12.5px] text-text-muted">
              {invoice.no} · Issued {shortDate(today)} · Due {shortDate(due)}
            </div>
          </div>
          <div className="text-right">
            <b>{fullName}</b>
            <div className="text-[12.5px] text-text-muted">{email}</div>
          </div>
        </div>
        <h4 className="mb-1 mt-4 text-[13px] uppercase text-text-muted">Bill to</h4>
        <div>{invoice.client || <span className="text-text-muted">Client name</span>}</div>
        <table className="mt-3 w-full border-collapse text-[14px]">
          <thead>
            <tr className="border-b border-border text-left">
              <th className="py-1.5">Description</th>
              <th className="py-1.5 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {items.length ? (
              items.map((item, i) => (
                <tr key={i} className="border-b border-border">
                  <td className="py-1.5">{item.description}</td>
                  <td className="py-1.5 text-right tabular">{money(item.amount)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={2} className="py-1.5 text-text-muted">
                  Add an item
                </td>
              </tr>
            )}
            <tr>
              <td className="py-1.5 font-bold">Total due</td>
              <td className="py-1.5 text-right font-bold tabular">{money(total)}</td>
            </tr>
          </tbody>
        </table>
        <h4 className="mb-1 mt-4 text-[13px] uppercase text-text-muted">How to pay</h4>
        <div>{invoice.payVia}</div>
        <p className="mt-3 text-[12.5px] text-text-muted">Thank you for your business.</p>
      </div>
      <div className="flex flex-wrap gap-2.5 print:hidden">
        <Button disabled={!passes || busy} onClick={onSave}>
          Save my invoice
        </Button>
        <Button variant="ghost" onClick={() => window.print()}>
          Print or save as PDF
        </Button>
        <BackButton to="/gig/4" label="Back to Step 4" />
      </div>
      {message ? <Banner tone="good">{message}</Banner> : null}
    </div>
  );
}
